import React, { createContext, useContext } from 'react';

const ChartContext = createContext({});

export const useChart = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }
  return context;
};

export const ChartContainer = ({ id, config, children, className = '', ...props }) => {
  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={id}
        className={`flex aspect-auto justify-center text-xs [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-gray-200/50
        [&_.recharts-cartesian-grid_line]:stroke-gray-200
        dark:[&_.recharts-cartesian-grid_line]:stroke-gray-700
        [&_.recharts-curve.recharts-tooltip-cursor]:stroke-gray-200
        dark:[&_.recharts-curve.recharts-tooltip-cursor]:stroke-gray-700
        [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-gray-200
        dark:[&_.recharts-polar-grid_[stroke='#ccc']]:stroke-gray-700
        [&_.recharts-radial-bar-background-sector]:fill-gray-100
        dark:[&_.recharts-radial-bar-background-sector]:fill-gray-800
        [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-gray-100
        dark:[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-gray-800
        [&_.recharts-reference-line_[stroke='#ccc']]:stroke-gray-200
        dark:[&_.recharts-reference-line_[stroke='#ccc']]:stroke-gray-700
        [&_.recharts-sector[stroke='#fff']]:stroke-white
        dark:[&_.recharts-sector[stroke='#fff']]:stroke-gray-950
        [&_.recharts-sector]:outline-none
        [&_.recharts-surface]:outline-none ${className}`}
        {...props}
      >
        {children}
      </div>
    </ChartContext.Provider>
  );
};

export const ChartTooltip = ({ content, ...props }) => {
  return (
    <div {...props}>
      {content}
    </div>
  );
};

export const ChartTooltipContent = ({
  active,
  payload = [],
  label,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  className = '',
  labelFormatter,
  formatter
}) => {
  const { config } = useChart();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2.5 shadow-xl ${className}`}>
      {!hideLabel && label && (
        <div className="font-medium text-gray-900 dark:text-gray-100 mb-1.5">
          {labelFormatter ? labelFormatter(label, payload) : label}
        </div>
      )}
      <div className="space-y-1">
        {payload.map((item, index) => {
          const itemConfig = config?.[item.dataKey] || {};
          const color = item.color || itemConfig.color || 'var(--chart-1)';

          return (
            <div key={`tooltip-${index}`} className="flex items-center gap-2">
              {!hideIndicator && indicator === 'dot' && (
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
              )}
              {!hideIndicator && indicator === 'line' && (
                <div
                  className="w-2.5 h-0.5"
                  style={{ backgroundColor: color }}
                />
              )}
              {!hideIndicator && indicator === 'dashed' && (
                <div
                  className="w-2.5 h-0.5 border-t-2 border-dashed"
                  style={{ borderColor: color }}
                />
              )}
              <div className="flex flex-1 justify-between gap-4">
                <span className="text-gray-600 dark:text-gray-400">
                  {itemConfig.label || item.name}
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {formatter ? formatter(item.value, item.name, item, index, payload) : item.value}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

